// frontend/src/graphql/tourBuilder.js
//
// CHANGES FROM PREVIOUS VERSION
// ──────────────────────────────
// - Added GET_SUGGEST_ITINERARY query for the GA optimizer endpoint.
// - Updated SUBMIT_CUSTOM_TOUR_REQUEST input to include parkNightsJson,
//   migrationZonesJson, and itinerarySource so custom_tour_service.py
//   can save itinerary_plan correctly.
// - All other queries unchanged.

import { gql } from '@apollo/client';

// ── Countries ─────────────────────────────────────────────────────────────────

export const GET_COUNTRIES_BY_CONTINENT = gql`
  query GetCountriesByContinent {
    tourCountries {
      id name slug code
      frontendContinentSlug continentName flagImage
    }
  }
`;

// ── Experience categories ──────────────────────────────────────────────────────

export const GET_EXPERIENCE_CATEGORIES = gql`
  query GetExperienceCategories {
    experienceCategories { id name slug icon }
  }
`;

// ── Experiences by country ─────────────────────────────────────────────────────

export const GET_EXPERIENCES_BY_COUNTRY = gql`
  query GetExperiencesByCountry($countrySlug: String!) {
    experiencesByCountry(countrySlug: $countrySlug, source: "curated") {
      id name slug description shortDescription
      category { name slug icon }
      difficulty durationDisplay pricePerPerson priceOnRequest featuredImage
      park { id slug name }
      culturalSite { id slug name }
    }
  }
`;

// ── Properties by country ──────────────────────────────────────────────────────

export const GET_PROPERTIES_BY_COUNTRY = gql`
  query GetPropertiesByCountry($countrySlug: String!, $experienceIds: [ID]) {
    propertiesByCountry(countrySlug: $countrySlug experienceIds: $experienceIds) {
      id name slug shortDescription featuredImage
      isFeatured isVerified averageRating amenities locationType
      park { id name slug }
      propertyType { id name slug icon }
      level levelLabel
      defaultBoardBasis { id name slug }
      roomTypes { id name pricePerNight maxOccupancy standardOccupancy featuredImage }
    }
  }
`;

// ── Serengeti migration zones ──────────────────────────────────────────────────

export const GET_SERENGETI_ZONES = gql`
  query GetSerengetiZones($parkSlug: String!) {
    parkMigrationZones(parkSlug: $parkSlug) {
      id zoneName seasonType monthFrom monthTo locationKeywords priority
    }
  }
`;

// ── Board basis (meal plans) ───────────────────────────────────────────────────

export const GET_BOARD_BASIS_OPTIONS = gql`
  query GetBoardBasisOptions {
    boardBasisOptions {
      id name slug icon description priceSupplementPerPersonPerNight
    }
  }
`;

// ── Transfer types ─────────────────────────────────────────────────────────────

export const GET_TRANSFER_TYPES = gql`
  query GetTransferTypes {
    transferTypes { id name slug description icon pricePerNight isDefault }
  }
`;

// ── Travel paces ───────────────────────────────────────────────────────────────

export const GET_TRAVEL_PACES = gql`
  query GetTravelPaces {
    travelPaces { id name slug description subLabel icon isDefault }
  }
`;

// ── Insurance plans ────────────────────────────────────────────────────────────

export const GET_INSURANCE_PLANS = gql`
  query GetInsurancePlans {
    insurancePlans {
      id name slug description ratePerPersonPerDay coverageAmountUsd coverageItems
    }
  }
`;

// ── Dietary options ────────────────────────────────────────────────────────────

export const GET_DIETARY_OPTIONS = gql`
  query GetDietaryOptions {
    dietaryOptions { id name slug }
  }
`;

// ── Operator seasons ───────────────────────────────────────────────────────────

export const GET_OPERATOR_SEASONS = gql`
  query GetOperatorSeasons {
    operatorSeasons {
      id name seasonType monthFrom monthTo color
      packageMarkupPercent discountPercent isDefault
      overrides {
        id dateFrom dateTo year packageMarkupPercent discountPercent
      }
    }
  }
`;

// ── GA itinerary suggestion ────────────────────────────────────────────────────
//
// Fires on the "Suggest Itinerary" button in Step 2.
// Returns suggestedParks which the frontend uses to pre-populate:
//   selectedParks, parkNights, selectedSerengetiZones
//
// preferences is optional — all fields have sensible defaults.

export const GET_SUGGEST_ITINERARY = gql`
  query GetSuggestItinerary(
    $countrySlugs:      [String!]!
    $touristComposition:[TouristCompositionInput!]!
    $startDate:         Date!
    $endDate:           Date!
    $preferences:       ItineraryPreferencesInput
  ) {
    suggestItinerary(
      countrySlugs:       $countrySlugs
      touristComposition: $touristComposition
      startDate:          $startDate
      endDate:            $endDate
      preferences:        $preferences
    ) {
      suggestedParks {
        parkId
        parkName
        parkSlug
        nights
        propertyId
        migrationZones
      }
      totalDistanceKm
      fitnessScore
      routeWarnings
      source
    }
  }
`;

// ── Pricing summary (lightweight sidebar estimate) ────────────────────────────

export const GET_CUSTOM_TOUR_PRICING_SUMMARY = gql`
  query GetCustomTourPricingSummary(
    $countrySlugs:        [String!]!
    $touristComposition:  [TouristCompositionInput!]!
    $startDate:           Date!
    $endDate:             Date!
    $selectedPropertyIds: [ID!]
    $transferTypeId:      ID
    $insuranceId:         ID
    $parkNightsJson:      String
    $migrationZonesJson:  String
  ) {
    customTourPricingSummary(
      countrySlugs:        $countrySlugs
      touristComposition:  $touristComposition
      startDate:           $startDate
      endDate:             $endDate
      selectedPropertyIds: $selectedPropertyIds
      transferTypeId:      $transferTypeId
      insuranceId:         $insuranceId
      parkNightsJson:      $parkNightsJson
      migrationZonesJson:  $migrationZonesJson
    ) {
      totalPrice pricePerPerson subtotal tax taxLabel currency
      isEstimate estimateBasis
      seasonsApplied { name seasonType color nights adjustment }
    }
  }
`;

// ── Pricing preview (full three-layer breakdown — Step 6) ─────────────────────

export const GET_CUSTOM_TOUR_PRICING_PREVIEW = gql`
  query GetCustomTourPricingPreview(
    $countrySlugs:            [String!]!
    $touristComposition:      [TouristCompositionInput!]!
    $startDate:               Date!
    $endDate:                 Date!
    $selectedPropertyIds:     [ID!]!
    $selectedExperienceIds:   [ID!]
    $selectedParkIds:         [ID!]
    $parkNightsJson:          String
    $migrationZonesJson:      String
    $transferTypeId:          ID
    $insuranceId:             ID
    $boardBasisSlugs:         [String!]
    $includeFlightAssistance: Boolean
    $channel:                 String
  ) {
    customTourPricingPreview(
      countrySlugs:            $countrySlugs
      touristComposition:      $touristComposition
      startDate:               $startDate
      endDate:                 $endDate
      selectedPropertyIds:     $selectedPropertyIds
      selectedExperienceIds:   $selectedExperienceIds
      selectedParkIds:         $selectedParkIds
      parkNightsJson:          $parkNightsJson
      migrationZonesJson:      $migrationZonesJson
      transferTypeId:          $transferTypeId
      insuranceId:             $insuranceId
      boardBasisSlugs:         $boardBasisSlugs
      includeFlightAssistance: $includeFlightAssistance
      channel:                 $channel
    ) {
      totalPrice pricePerPerson subtotal tax taxLabel currency
      breakdown {
        agencyService {
          feeLabel feePerPerson tourists touristComposition
          gross afterDiscount net
          adjustments { type name seasonType nights amount direction }
        }
        parkFees {
          date park seasonType migrationZone
          fees {
            type visitorCategory nationalities count
            unitAmount unitAmountGross subtotal subtotalGross
            vatAmount currency migrationZone seasonApplied
          }
          dayTotalNet dayTotalGross dayTotalVat
        }
        experiences {
          items {
            experienceId name category country source
            pricePerPerson tourists lineTotal durationDisplay priceOnRequest
          }
          total count hasPriceOnRequestItems note
        }
        boardBasis { name ratePerPersonPerNight tourists nights total }
        transfer   { name ratePerNight nights total }
        insurance  { name ratePerPersonPerDay tourists nights total }
        vehicle    { description charge rate days vehicles total note }
        operatorSeasonBreakdown {
          date seasonName seasonType markupPct discountPct
          baseFee adjustment channelActive source
        }
        summary {
          agencyServiceNet statutoryParkFees statutoryParkFeesNet
          statutoryParkFeesVat experiencesTotal subtotal taxAmount total perPerson
        }
      }
    }
  }
`;

// ── Mutations ──────────────────────────────────────────────────────────────────

export const CREATE_CUSTOM_EXPERIENCE = gql`
  mutation CreateCustomExperience($input: CreateCustomExperienceInput!) {
    createCustomExperience(input: $input) {
      success
      experience {
        id name slug difficulty durationDisplay pricePerPerson priceOnRequest
        category { name icon }
      }
    }
  }
`;

export const DELETE_CUSTOM_EXPERIENCE = gql`
  mutation DeleteCustomExperience($experienceId: ID! $customTourRequestId: ID!) {
    deleteCustomExperience(
      experienceId: $experienceId
      customTourRequestId: $customTourRequestId
    ) { success }
  }
`;

// UPDATED: added parkNightsJson, migrationZonesJson, itinerarySource
// so custom_tour_service.py can save itinerary_plan correctly.
export const SUBMIT_CUSTOM_TOUR_REQUEST = gql`
  mutation SubmitCustomTourRequest($input: CustomTourRequestInput!) {
    submitCustomTourRequest(input: $input) {
      success
      message
      expiresAt
      customTourRequest {
        id status guestName guestEmail guestPhone calculatedTotal expiresAt
      }
    }
  }
`;

// ── Legacy packaged-tour pricing preview (BookingPage) ────────────────────────


// ── Parks by country ──────────────────────────────────────────────────────────

export const GET_PARKS_BY_COUNTRY = gql`
  query GetParksByCountry($countrySlug: String!) {
    parks(countrySlug: $countrySlug) {
      id name slug shortDescription featuredImage isFeatured
      bestMonths mainAttractions availableActivities
      hasAirstrip hasCamping requiresGuide
      region { id name slug }
      circuit { id name slug }
    }
  }
`;

// ── Properties filtered by a single park ─────────────────────────────────────

export const GET_PROPERTIES_BY_PARK = gql`
  query GetPropertiesByPark($countrySlug: String!, $parkId: ID!) {
    propertiesByCountry(countrySlug: $countrySlug, parkId: $parkId) {
      id 
      name 
      slug 
      shortDescription 
      featuredImage
      isFeatured 
      isVerified 
      averageRating 
      amenities 
      locationType
      park { id name slug }
      propertyType { id name slug icon }
      level levelLabel
      roomTypes { id name pricePerNight maxOccupancy standardOccupancy featuredImage }
    }
  }
`;
/** A ready-made safari, shaped for "customise this safari": its stays
 *  (park, nights, lodge) become the builder's starting blueprint. */
export const GET_TOUR_FOR_CUSTOMISE = gql`
  query TourForCustomise($slug: String!) {
    tour(slug: $slug) {
      id slug name durationDays durationNights
      itineraryDays {
        dayNumber isOvernight
        primaryPark { id name slug country { name slug } }
        accommodation { id name }
      }
    }
  }
`;

/** Where the Great Migration herds usually are, month by month — the one
 *  calendar the builder, suggestions and itinerary page all use. */
export const GET_GREAT_MIGRATION = gql`
  query GreatMigration {
    greatMigration { month where moment zones { key label strength } }
  }
`;
